import { isNative } from '~/plugins/native/capacitor'

// Initialise over the air updates from our self hosted OTA system
export default async function ({ store, app, $log, redirect, route }, inject) {
  const otaUpdates = {}

  if (isNative) {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater')
    const { SplashScreen } = await import('@capacitor/splash-screen')

    let data = null

    $log.debug('OTA notifying ready')

    CapacitorUpdater.notifyAppReady()

    // Remove stale OTA bundles so an install's "Documents & Data" doesn't balloon over
    // time. We keep the running bundle, the built-in bundle, any bundle queued to load on
    // next launch (status "pending"), and the most recent previous successful bundle so a
    // rollback is still possible. Everything else is deleted.
    otaUpdates.cleanupOldBundles = async () => {
      try {
        const current = await CapacitorUpdater.current()
        const currentId = current?.bundle?.id

        $log.debug('OTA cleanup: current bundle', {
          id: currentId,
          version: current?.bundle?.version,
          status: current?.bundle?.status,
          native: current?.native
        })

        if (!currentId) {
          $log.debug('OTA cleanup: no current bundle id, skipping')

          return
        }

        const { bundles } = await CapacitorUpdater.list()

        $log.debug('OTA cleanup: bundles before', {
          count: bundles?.length ?? 0,
          bundles: (bundles ?? []).map(bundle => ({
            id: bundle.id,
            version: bundle.version,
            status: bundle.status,
            downloaded: bundle.downloaded
          }))
        })

        if (!bundles?.length) {
          return
        }

        const keepIds = new Set([currentId, 'builtin'])

        // Never remove a bundle that is queued to load on the next launch.
        bundles.filter(bundle => bundle.status === 'pending').forEach(bundle => keepIds.add(bundle.id))

        // Keep the most recently downloaded previous successful bundle for rollback.
        const [previousSuccessfulBundle] = bundles
          .filter(bundle => bundle.status === 'success' && bundle.id !== currentId && bundle.id !== 'builtin')
          .sort((a, b) => new Date(b.downloaded) - new Date(a.downloaded))

        if (previousSuccessfulBundle) {
          keepIds.add(previousSuccessfulBundle.id)
        }

        $log.debug('OTA cleanup: keeping bundles', [...keepIds])

        const deleted = []
        const failed = []

        for (const bundle of bundles) {
          if (keepIds.has(bundle.id)) {
            continue
          }

          try {
            await CapacitorUpdater.delete({ id: bundle.id })

            deleted.push({ id: bundle.id, version: bundle.version })

            $log.debug('OTA cleanup: deleted bundle', { id: bundle.id, version: bundle.version })
          } catch (error) {
            failed.push({ id: bundle.id, version: bundle.version, error: error?.message })

            $log.debug('OTA cleanup: failed to delete bundle', {
              id: bundle.id,
              version: bundle.version,
              error: error?.message
            })
          }
        }

        // Re-list after deletion so the saved logs show whether anything was actually reclaimed.
        const after = await CapacitorUpdater.list()

        $log.debug('OTA cleanup: summary', {
          deletedCount: deleted.length,
          failedCount: failed.length,
          remainingCount: after?.bundles?.length ?? 0,
          remaining: (after?.bundles ?? []).map(bundle => ({
            id: bundle.id,
            version: bundle.version,
            status: bundle.status
          }))
        })
      } catch (error) {
        $log.debug('OTA cleanup: error', { error: error?.message })
      }
    }

    // Walk the app's sandbox directories and report how much disk each one uses. iOS
    // "Documents & Data" covers far more than OTA bundles (WebView storage, caches,
    // persisted state), so this breakdown tells us which folder is actually the culprit.
    otaUpdates.reportStorageUsage = async () => {
      try {
        const { Filesystem, Directory } = await import('@capacitor/filesystem')

        const formatBytes = bytes => {
          if (!bytes) {
            return '0 B'
          }

          const units = ['B', 'KB', 'MB', 'GB']
          const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)

          return `${(bytes / Math.pow(1024, exponent)).toFixed(1)} ${units[exponent]}`
        }

        // Recursively sum every file under a directory. Depth is capped so a deep tree
        // can't spin forever, and unreadable paths are skipped rather than aborting.
        const measure = async (directory, path, depth = 0) => {
          let total = 0
          let entries = []

          try {
            const result = await Filesystem.readdir({ directory, path })

            entries = result.files ?? []
          } catch (error) {
            return total
          }

          for (const entry of entries) {
            const entryPath = `${path}/${entry.name}`

            if (entry.type === 'directory') {
              if (depth < 6) {
                total += await measure(directory, entryPath, depth + 1)
              }
            } else {
              total += entry.size ?? 0
            }
          }

          return total
        }

        const roots = [
          { label: 'Data', directory: Directory.Data },
          { label: 'Documents', directory: Directory.Documents },
          { label: 'Library', directory: Directory.Library },
          { label: 'Cache', directory: Directory.Cache }
        ]

        const report = {}

        for (const root of roots) {
          try {
            const { files } = await Filesystem.readdir({ directory: root.directory, path: '.' })

            const children = {}
            let total = 0

            for (const entry of files ?? []) {
              const size =
                entry.type === 'directory' ? await measure(root.directory, entry.name, 1) : entry.size ?? 0

              children[entry.name] = formatBytes(size)
              total += size
            }

            report[root.label] = { total: formatBytes(total), children }
          } catch (error) {
            report[root.label] = { error: error?.message }
          }
        }

        $log.debug('OTA storage: filesystem usage', report)
      } catch (error) {
        $log.debug('OTA storage: report error', { error: error?.message })
      }
    }

    otaUpdates.sync = async isActive => {
      if (process.env.APP_ENVIRONMENT === 'local') {
        return false
      }

      const currentBundle = await CapacitorUpdater.current()

      if (currentBundle.bundle?.version) {
        store.commit('device/setBundleVersion', currentBundle.bundle.version)
      }

      const isOnCheckoutPage = app.router.history?.current?.path?.includes('checkout')
      const isOnAuthPage = app.router.history?.current?.path?.includes('auth/')

      if (isActive) {
        store.commit('overTheAirUpdates/setStatusCode', 5)

        try {
          const latest = await CapacitorUpdater.getLatest()

          // $log.debug('OTA latest', latest)

          if (latest.url) {
            store.commit('overTheAirUpdates/setStatusCode', 7)

            $log.debug('OTA downloading...')

            data = await CapacitorUpdater.download({
              url: latest.url,
              version: latest.version
            })

            $log.debug('OTA finished downloading')

            store.commit('overTheAirUpdates/setStatusCode', 6)
          } else {
            store.commit('overTheAirUpdates/setStatusCode', 0)
          }
        } catch (error) {
          console.log('OTA threw error', error)

          store.commit('overTheAirUpdates/setStatusCode', 0)
        }
      } else if (!isActive && data && !isOnCheckoutPage && !isOnAuthPage) {
        $log.debug('OTA: Setting new version')
        // Do the switch when user leave app or when you want
        SplashScreen.show()

        try {
          store.commit('overTheAirUpdates/setStatusCode', 8)

          await CapacitorUpdater.set({ id: data.id })

          store.commit('overTheAirUpdates/setStatusCode', 1)

          redirect('/platform')
        } catch (error) {
          $log.debug('OTA error', error)

          store.commit('overTheAirUpdates/setStatusCode', 3)

          store.commit('overTheAirUpdates/setError', error)

          SplashScreen.hide()
        }
      }
    }

    otaUpdates.setUserId = async userId => {
      if (!userId) {
        return false
      }

      await CapacitorUpdater.setCustomId({ customId: userId })
    }

    // Run sync first, then prune bundles once it settles so cleanup never races a
    // download in progress. Catching sync errors avoids unhandled-rejection warnings.
    otaUpdates
      .sync(true)
      .catch(error => $log.debug('OTA sync error', error))
      .then(() => otaUpdates.cleanupOldBundles())
      .then(() => otaUpdates.reportStorageUsage())

    const { App } = await import('@capacitor/app')

    App.addListener('appStateChange', appState => {
      otaUpdates.sync(appState.isActive)
    })
  }

  inject('otaUpdates', otaUpdates)
}
